import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, EventType, RedirectRequest } from '@azure/msal-browser';
import { Observable, of, Subject, BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { B2C_CONFIG } from '../environments/environments.module';
import { ApplicationInsightService } from '../logger/application-insight.service';
import { isPlatformBrowser } from '@angular/common';
//import { AuthModule } from './auth.module';

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private readonly defaultScopes: string[];
  private readonly destroying$ = new Subject<void>();
  public authObserver$: Observable<boolean>;

  private userIdSubject = new BehaviorSubject<number>(0);
  userId$ = this.userIdSubject.asObservable();

  public loggedInUser = new BehaviorSubject<boolean>(false);
  user$ = this.loggedInUser.asObservable();
  private isBrowser: boolean = false;

  private roles: string[] = [];
  public menus: string[] = [];
  private menuSubject = new BehaviorSubject<any[]>([]);
  public menu$ = this.menuSubject.asObservable();

  private rolesSubject = new BehaviorSubject<string[]>([]);
  public roles$ = this.rolesSubject.asObservable();

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, private auth: MsalService,
    private msalBroadcastService: MsalBroadcastService, private logger: ApplicationInsightService,
    @Inject(B2C_CONFIG) private b2cConfig: any,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.defaultScopes = this.b2cConfig.apiScopes.employeeApi.scopes;
    if (this.authenticationEnabled()) {
      this.authObserver$ = this.msalBroadcastService.msalSubject$
        .pipe(

          map((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType == EventType.HANDLE_REDIRECT_END) {
              this.checkAndSetActiveAccount();
            }
            return (event.eventType === EventType.LOGIN_SUCCESS || event.eventType == EventType.HANDLE_REDIRECT_END);
          }),
          takeUntil(this.destroying$)
        );
    } else {
      this.authObserver$ = of(true);
    }
  }

  public isUserAuthenticated(): boolean {
    return this.auth.instance.getAllAccounts().length > 0;
  }

  public authenticationEnabled(): boolean {
    return this.b2cConfig.isAuthenticationEnabled;
  }

  checkAndSetActiveAccount(): void {
    if (this.authenticationEnabled()) {
      const activeAccount = this.auth.instance.getActiveAccount();

      if (!activeAccount) {
        if (this.isUserAuthenticated()) {
          const accounts = this.auth.instance.getAllAccounts();
          this.auth.instance.setActiveAccount(accounts[0]);
          this.logger.logTrace("Login Successful", { data: accounts })
        } else {
          if (this.msalGuardConfig.authRequest) {
            this.auth.loginRedirect({
              ...this.msalGuardConfig.authRequest,
            } as RedirectRequest);
          } else {
            this.auth.loginRedirect();
          }
        }
      }
    }
  }

  public authRedirect(): Observable<AuthenticationResult> {
    return this.auth.handleRedirectObservable()
  }

  public logout(): void {
    this.auth.logout();
  }

  public getUser(): string | undefined {
    return this.auth.instance.getActiveAccount()?.username;
  }

  public getUserDetails(): { enterpriseId: string; name: string } {
    if (!this.isBrowser || !this.isUserAuthenticated()) {
      return { enterpriseId: '', name: '' };
    }
    const accounts = this.auth.instance.getAllAccounts();
    // console.log('User Accounts:', accounts);
    if (!accounts[0]) return { enterpriseId: '', name: '' };
    if (accounts[0]?.username?.toLowerCase().endsWith('@accenture.com')) {
      return {
        enterpriseId: accounts[0]?.username.split('@')[0],
        name: accounts[0].name ?? ''
      };
    } else {
      return {
        enterpriseId: accounts[0]?.username,
        name: accounts[0].name ?? ''
      };
    }
  }

  public async getAccessToken(scopes: string[] = this.defaultScopes): Promise<string> {
    var currentAccount = this.auth.instance.getAllAccounts()[0];
    var silentRequest = {
      scopes: scopes,
      account: currentAccount,
      forceRefresh: false
    };

    try {
      const res = await firstValueFrom(this.auth.acquireTokenSilent(silentRequest));
      return res.accessToken ?? '';
    } catch (error) {
      console.error('Error acquiring token silently:', error);
      return '';
    }
  }

  public async getIdToken(scopes: string[] = this.defaultScopes): Promise<string> {
    var currentAccount = this.auth.instance.getAllAccounts()[0];
    var idToken;
    var silentRequest = {
      scopes: scopes,
      account: currentAccount,
      forceRefresh: false
    };
    await this.auth.acquireTokenSilent(silentRequest).subscribe((res) => {
      idToken = res.idToken;
    });
    return idToken ?? '';
  }

  setMenu(menus: string[]): void {
    this.menus = menus;
    this.menuSubject.next(menus);
    if (this.isBrowser) {
      localStorage.setItem('menus', JSON.stringify(menus));
    }
  }

  setRoles(roles: string[]): void {
    this.roles = roles;
    this.rolesSubject.next(roles);
    if (this.isBrowser) {
      localStorage.setItem('roles', JSON.stringify(roles));
    }
  }


  getRoles(): string[] {
    if (this.isBrowser) {
      //return this.roles.length ? this.roles : JSON.parse(localStorage.getItem('roles') || '[]');
      const stored = JSON.parse(localStorage.getItem('roles') || '[]');
      return this.roles.length ? this.roles : stored;
    }
      return [];
  }

  public getUserClaims(): undefined | unknown {
    return this.auth.instance.getAllAccounts()[0]?.idTokenClaims;
  }
}
